import type { FmBannerTone } from "./FmBanner";
import type { FmButtonSize, FmButtonVariant } from "./FmButton";
import type { FmFormFlowPageDescriptor } from "./FmFormFlowPage.types";
import type { FmUnlockPreviewDescriptor } from "./FmUnlockPreview";
import type { FmSection } from "./sections/section.types";
import type { FmFactsElement, FmTableElement } from "./elements/elements.types";
import type { FmOperationalMetricDescriptor } from "./operationalSectionRecipes";
import type { ContextualForestMediaRequest } from "@/shared/config/contextualForestMedia";

/** Region band tones; a section region passes its tone to its embedded section. */
export const FM_OPERATIONAL_REGION_BAND_TONES = ["light", "dark", "paper"] as const;
export type FmOperationalRegionBandTone = (typeof FM_OPERATIONAL_REGION_BAND_TONES)[number];

export type FmOperationalWorkspaceState =
  "ready" | "loading" | "empty" | "stale" | "blocked" | "error";

/**
 * Scope a state surface owns. `stage`/`region` scoped surfaces render in place
 * and every other region keeps rendering its served data; only `workspace`
 * scope replaces the region body.
 */
export type FmOperationalStateScopeDescriptor =
  | { kind: "workspace" }
  | { kind: "stage"; stageId: string }
  | { kind: "region"; regionId: string };

export interface FmOperationalEmptyStateDescriptor {
  /** Why nothing is served yet, in owner language. */
  description: string;
  /** Control that creates the missing thing; it leads the empty surface. */
  primaryActionId: string;
}

export interface FmOperationalStaleStateDescriptor {
  /**
   * The notice's own title, as a STATEMENT. Every other notice in the archetype
   * leads with one ("Stand 12 has no served access route", "Candidate comparison
   * could not be served"); this one used the revision LABEL, so the one notice
   * whose meaning is least obvious was also the only one that did not say what
   * had happened.
   */
  name: string;
  /** What is older than the served revision, in owner language. */
  description: string;
  /** Control that re-resolves the served revision. */
  refreshActionId: string;
}

export interface FmOperationalErrorStateDescriptor {
  /** Names the scope that failed (e.g. the failing region's own title). */
  scopeLabel: string;
  /** What the owner can conclude and what is still trustworthy. */
  description: string;
  /** Control that retries the failing scope. */
  retryActionId: string;
  /**
   * Regions that could not be served. Every other region keeps rendering its
   * served data. An empty list means the whole workspace failed to load.
   */
  failedRegionIds: readonly string[];
}

export interface FmOperationalBlockerDescriptor {
  id: string;
  /** The blocker's own name — never the workspace state label. */
  name: string;
  /** Why the owner is blocked. */
  reason: string;
  /** Control that resolves this blocker; it renders inside the blocker. */
  resolvingActionId: string;
  scope: FmOperationalStateScopeDescriptor;
}

/**
 * A capability this owner's package does not include, shown as a PREVIEW rather
 * than as a refusal (capability gap G5).
 *
 * `operations-planning.md` acceptance criterion 1 is explicit: "every paid action
 * renders as a labelled preview card with the Plot Package price and a one-click
 * route to the price card — never a disabled blank or a redirect." The archetype
 * states a paid boundary as a preview card, not by disabling the control and
 * putting the reason in a `title` attribute (that is the disabled blank).
 *
 * The card itself is the SHARED `FmUnlockPreview` adapter that FmFormFlowPage
 * already consumes. It is deliberately not forked per archetype: one paid
 * boundary, one presentation, one place to change the price copy.
 */
export interface FmOperationalPaidBoundaryDescriptor {
  id: string;
  /** Where the withheld capability lives, so the card renders beside it. */
  scope: FmOperationalStateScopeDescriptor;
  preview: FmUnlockPreviewDescriptor;
}

export type FmOperationalStageStatus =
  "available" | "current" | "complete" | "blocked";

export interface FmOperationalStageDescriptor {
  id: string;
  label: string;
  description?: string;
  ownerQuestion: string;
  literalAnswer: string;
  /**
   * The one figure that answers the owner's question, shown with the answer
   * so the page leads with it instead of below the fold. A figure the server
   * has not produced is `value: null` and names why in `caption`.
   */
  answerFigure?: FmOperationalMetricDescriptor;
  dominantRegionId: string;
  transition: string;
  /** Optional labelled move to the next declared stage after reviewing this one. */
  transitionActionLabel?: string;
  mobileRegionOrder: readonly string[];
  conversionRole: "orient" | "evaluate" | "commit";
  status: FmOperationalStageStatus;
  disabledReason?: string;
  regionIds: readonly string[];
  controlIds: readonly string[];
}

/** One choice the owner made at a stage, kept in view on every later stage. */
export interface FmOperationalChoiceSummaryItem {
  stageId: string;
  label: string;
  value: string;
}

/** What the choices made so far add up to, kept in view on every later step. */
export interface FmOperationalChoiceSummary {
  title: string;
  /** Names the move back to the step that made a choice. */
  changeLabel: string;
  items: readonly [FmOperationalChoiceSummaryItem, ...FmOperationalChoiceSummaryItem[]];
  /**
   * What the choices add up to: a facts list or a table (e.g. harvest volume
   * by species and assortment), read after the step's own work. The flow
   * serves it on the steps where the owner weighs it; a later step that
   * only needs the total keeps it as a summary item instead of repeating
   * the table. Served figures only; a value the server has not produced is
   * the element's own unavailable state, never a placeholder.
   */
  outcome?: FmFactsElement | FmTableElement;
  /**
   * The choices are now a record (e.g. the request is saved): the summary
   * stays in view but offers no way back to change them, and the step has
   * no move back.
   */
  readOnly?: boolean;
}

/**
 * The one figure the compact choice strip opens with (e.g. the sale's net
 * euros), at figure weight ahead of the quiet choices. `value` is the served
 * figure already formatted for the locale; the unit and caption ride beside it.
 */
export interface FmOperationalChoiceSummaryLead extends FmOperationalChoiceSummaryItem {
  unit?: string;
  caption?: string;
}

/**
 * The compact strip's summary: the standard summary plus its lead figure and
 * the phone disclosure. Below the phone breakpoint the strip is the lead and
 * one control; `expandLabel` / `collapseLabel` name the control that shows
 * every choice with its way back.
 */
export interface FmOperationalCompactChoiceSummary extends FmOperationalChoiceSummary {
  lead?: FmOperationalChoiceSummaryLead;
  expandLabel: string;
  collapseLabel: string;
}

interface FmOperationalGuidanceBase {
  /** Orientation line; carries `{index}`, `{count}` and `{label}`. */
  progressLabel: string;
  /** The move back to the previous step. */
  previousLabel: string;
}

/**
 * `standard`: the page header with its status rail, the stage strip as a
 * progress stepper under it, the choices as one panel above each step, and
 * the back/forward pair at the foot of the step (docked only on a phone).
 */
export interface FmOperationalStandardGuidanceDescriptor extends FmOperationalGuidanceBase {
  chrome?: "standard";
  summary?: FmOperationalChoiceSummary;
}

/**
 * `compact`: a one-line page header on the first step and none from the
 * second; the stepper and the revision chip in one docked 56 px bar; the
 * choices as one strip under it led by `summary.lead`; the back/forward pair
 * docked to the bottom edge at every width.
 */
export interface FmOperationalCompactGuidanceDescriptor extends FmOperationalGuidanceBase {
  chrome: "compact";
  summary?: FmOperationalCompactChoiceSummary;
}

/**
 * Guided flow: the stages are steps taken in order. The stage strip becomes a
 * progress stepper, each step offers one primary move forward and one move
 * back, and the choices made so far stay in view as a summary. Opt-in for a
 * linear owner flow (operations planning, tender); a non-linear workspace
 * keeps the plain stage strip. `chrome` is the one variant seam: it owns the
 * header, the stepper, the action dock and the summary presentation.
 */
export type FmOperationalGuidanceDescriptor =
  | FmOperationalStandardGuidanceDescriptor
  | FmOperationalCompactGuidanceDescriptor;

/** The chrome variant a guided workspace renders. */
export type FmOperationalGuidanceChrome = "standard" | "compact";


export interface FmOperationalRevisionDescriptor {
  id: string;
  version: number;
  label: string;
  status: "current" | "stale" | "conflict";
  observedAt?: string;
}

export type FmOperationalSelectionDescriptor =
  | { kind: "none"; revisionId: string }
  | {
      kind: "entity";
      entityKind:
        "property" | "stand" | "plan" | "campaign" | "plot" | "tender";
      entityId: string;
      label: string;
      revisionId: string;
    };

export interface FmOperationalActionDescriptor {
  id: string;
  label: string;
  variant?: FmButtonVariant;
  /** Size follows action weight: commit actions may use the established md button. */
  size?: FmButtonSize;
  /**
   * `destructive` marks a trigger whose dialog is destructive, so the TRIGGER
   * carries the tone too. "Delete this plan" rendered as plain text beside "Open
   * Implement", styled identically — nothing told the owner one navigates and
   * the other removes a plan and its seven simulations, and the danger language
   * appeared only after the click.
   */
  tone?: "default" | "destructive";
  disabled?: boolean;
  disabledReason?: string;
  availableWhenUnavailable?: boolean;
  expectedRevisionId: string;
  expectedPayloadRevision?: string;
  expectedDeleteConsequenceRevision?: string;
  confirmationId?: string;
}

export interface FmOperationalBannerDescriptor {
  id: string;
  tone: FmBannerTone;
  title: string;
  description?: string;
}

interface FmOperationalReceiptBase {
  id: string;
  /**
   * Machine code for telemetry and tests only. The renderer never prints it to
   * an owner; it is exposed as a `data-code` attribute.
   */
  code: string;
  message: string;
}

export type FmOperationalReceiptDescriptor =
  | (FmOperationalReceiptBase & {
      kind: "blocker";
      /** Control that resolves this blocker; it renders inside the receipt. */
      resolvingActionId: string;
    })
  | (FmOperationalReceiptBase & {
      kind: "success" | "information";
      resolvingActionId?: never;
    });

interface FmOperationalMutationBase {
  label: string;
  receipts: readonly FmOperationalReceiptDescriptor[];
}

export type FmOperationalMutationDescriptor =
  | (FmOperationalMutationBase & { status: "pending"; progress?: number })
  | (FmOperationalMutationBase & {
      status: "idle" | "succeeded" | "failed" | "conflict";
      progress?: never;
    });

/**
 * Whether a region's values describe the selected entity or the workspace as a
 * whole. A `selection` scoped region may not carry entity values while
 * `selection.kind === 'none'`.
 */
export type FmOperationalRegionScope = "selection" | "workspace";

export interface FmOperationalFilterOptionDescriptor {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FmOperationalSelectFilterDescriptor {
  kind: "select";
  id: string;
  label: string;
  value: string;
  options: readonly FmOperationalFilterOptionDescriptor[];
  searchable?: boolean;
}

export type FmOperationalFilterDescriptor = FmOperationalSelectFilterDescriptor;

export interface FmOperationalWorkspaceVocabularyDescriptor {
  workspace: string;
  dataRevision: string;
  status: string;
  /**
   * Short served status tokens — one per workspace state. The status rail shows
   * one of these (or the active mutation's own label); it never shows the long
   * `stateLabel` sentence, which belongs to the stage lede.
   */
  statuses: Readonly<Record<FmOperationalWorkspaceState, string>>;
  rowActions: string;
  /**
   * Served copy for a region whose content failed to render. The failure stays
   * inside that region, so the rest of the workspace keeps working.
   */
  regionFailure: { title: string; description: string };
  /**
   * Served copy for the whole route failing to render. Required: the root always
   * wraps the shell contract in a route boundary (a declared `renderFailure`
   * overrides this copy), so a descriptor contract failure never unmounts the
   * page to blank.
   */
  routeFailure: { title: string; description: string; retryLabel: string };
}

/**
 * The one served sentence that frames a region's evidence: what the owner should
 * look at here and what it means. It is the region's LEDE — eyebrow, headline,
 * lede, media — and it is a plain served string, never a node.
 *
 * It is optional because some regions genuinely have nothing to add beyond their
 * title. When it is absent the renderer COLLAPSES the story rail and the media
 * takes the full width, so a heading can never strand itself over an empty
 * column.
 */
type FmOperationalRegionStory = {
  id: string;
  /** Optional authored chapter label. Never derived from kind, order, or scope. */
  eyebrow?: string;
  title: string;
  lede?: string;
  /**
   * WHOSE this region's content is. Declared on every kind so selection evidence
   * stays separate from property-wide evidence without exposing implementation
   * vocabulary in the page.
   */
  scope: FmOperationalRegionScope;
  /** How this region reads when it has nothing to show. */
  unavailable: FmOperationalRegionUnavailableDescriptor;
  /**
   * Abutting band: the region owns a full-bleed background. Dark is map canvas
   * and the create CTA only — never economics prose (§11 mandate 7). Paper is
   * working evidence (a ledger, an excluded-stands table, a terms sheet) one
   * step off light. Omit for light.
   */
  bandTone?: FmOperationalRegionBandTone;
};

/**
 * The ONE non-ready surface a region owns, at region scope
 *
 * 6.16 fixed this at workspace scope and left region scope on the old pattern:
 * a stranded left-aligned headline over a narrow band containing one centred
 * muted grey sentence of the form "<region title> is unavailable.", repeated
 * verbatim down the page, telling the owner neither why nor what to do. So the
 * template is gone and the shape is typed: name the missing THING, say what
 * produces it, and carry the control that produces it where one exists.
 */
export interface FmOperationalRegionUnavailableDescriptor {
  /** What is missing, named as the thing itself — never the region's own title. */
  title: string;
  /** One line: why it is not here, and what would produce it. */
  reason: string;
  /** Declared control that produces the missing thing, when one exists. */
  resolvingActionId?: string;
  /**
   * Machine-readable name for WHY this region serves nothing, stamped on the
   * region as `data-unavailable-reason`.
   *
   * A table region with no rows renders one banner whatever produced the
   * emptiness, so "still reading its data", "the read failed", "nothing is
   * selected yet" and "this really is empty" were indistinguishable to anything
   * reading the page — which is how a ledger that was still loading was read as
   * a plan with no stands for months. Name the reason and
   * the four states stop collapsing into one.
   */
  reasonId?: string;
}

/**
 * Region content. Every region composes a shared section of elements; table,
 * chart and map content are table, chart and map elements whose callbacks
 * dispatch the workspace's revisioned events. Only the handoff destinations
 * stay region-level, because they press declared workspace controls.
 */
export type FmOperationalRegionDescriptor =
  | (FmOperationalRegionStory & {
      kind: "section";
      section: FmSection;
    })
  | (FmOperationalRegionStory & {
      kind: "handoff";
      destinations: readonly FmOperationalDestinationDescriptor[];
    });

/**
 * One place this plan can go next. A heading that promises destinations must
 * deliver things the owner can press: the commit stage rendered four
 * eyebrow-and-value pairs the owner could not click, under the heading "Where
 * this plan goes next", which is also what the
 * ratified story means by "entry links only" — a link that is not a link is not
 * an entry.
 */
export type FmOperationalDestinationPurpose =
  | "implementation"
  | "analysis"
  | "measurement"
  | "market"
  | "overview"
  | "assistant";

export interface FmOperationalDestinationDescriptor {
  id: string;
  /** Page-neutral ownership/purpose; the renderer chooses icon, accent, and CTA weight. */
  purpose?: FmOperationalDestinationPurpose;
  /** Where the owner lands, named as the place. */
  label: string;
  /** What happens there, in one line. */
  outcome: string;
  /** Product or capability that owns the destination, shown as compact wayfinding. */
  ownerLabel?: string;
  /** Served readiness state for this destination in the current plan context. */
  availabilityLabel?: string;
  /** Declared control this destination activates. It may not be disabled. */
  actionId: string;
}

export interface FmOperationalDialogDescriptor {
  kind: "confirmation";
  id: string;
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  actionId: string;
  /** Optional second consequence for a three-way save/discard/stay decision. */
  alternateLabel?: string;
  alternateActionId?: string;
  alternateDestructive?: boolean;
  expectedRevisionId: string;
  expectedPayloadRevision?: string;
  expectedDeleteConsequenceRevision?: string;
  destructive?: boolean;
  /** Allow passive dismissal independently of the confirmation consequence tone. */
  dismissible?: boolean;
}

/** A linear creation workflow owned and rendered by the operational workspace. */
export interface FmOperationalFormFlowCapabilityDescriptor {
  id: string;
  open: boolean;
  title: string;
  closeLabel: string;
  size: "md" | "lg";
  flow: FmFormFlowPageDescriptor;
}

/** Copy for failures thrown while rendering the operational workspace. */
export interface FmOperationalRenderFailureDescriptor {
  title: string;
  description: string;
  retryLabel: string;
  resetKey: string;
  testId?: string;
}

export type FmOperationalWorkspaceEvent =
  | { kind: "stage-change"; stageId: string }
  | { kind: "action"; actionId: string; expectedRevisionId: string }
  | {
      kind: "filter-change";
      filterId: string;
      value: string;
      expectedRevisionId: string;
    }
  | {
      kind: "row-action";
      regionId: string;
      entityId: string;
      actionId: string;
      expectedRevisionId: string;
    }
  | { kind: "selection-change"; entityId: string; expectedRevisionId: string }
  | {
      kind: "dialog-confirm";
      dialogId: string;
      actionId: string;
      expectedRevisionId: string;
      expectedPayloadRevision?: string;
      expectedDeleteConsequenceRevision?: string;
    }
  | {
      kind: "dialog-alternate";
      dialogId: string;
      actionId: string;
      expectedRevisionId: string;
    }
  | { kind: "dialog-dismiss"; dialogId: string };

/**
 * Title masthead: a governed forest photograph behind the
 * workspace title. Species is resolved from served inventory; the renderer
 * owns crop and scrim. Omit when no illustrative identity can be resolved.
 */
export interface FmOperationalWorkspaceMastheadDescriptor {
  mediaContext: ContextualForestMediaRequest;
}

export interface FmOperationalReturnActionDescriptor {
  label: string;
  onActivate: () => void;
}

interface FmOperationalWorkspaceBase {
  id: string;
  ariaLabel: string;
  title: string;
  description?: string;
  masthead?: FmOperationalWorkspaceMastheadDescriptor;
  /** Safe, explicit return to the owner context that opened the workspace. */
  returnAction?: FmOperationalReturnActionDescriptor;
  vocabulary: FmOperationalWorkspaceVocabularyDescriptor;
  /**
   * The one long sentence naming the current state. It renders exactly once —
   * as the stage lede — never as the status rail value and never as the state
   * surface body, which carry their own served copy.
   */
  stateLabel: string;
  activeStageId: string;
  stages: readonly FmOperationalStageDescriptor[];
  /** Present only for a linear flow taken step by step. */
  guidance?: FmOperationalGuidanceDescriptor;
  revision: FmOperationalRevisionDescriptor;
  selection: FmOperationalSelectionDescriptor;
  mutation: FmOperationalMutationDescriptor;
  banners: readonly FmOperationalBannerDescriptor[];
  filters: readonly FmOperationalFilterDescriptor[];
  controls: readonly FmOperationalActionDescriptor[];
  regions: readonly FmOperationalRegionDescriptor[];
  /**
   * Capabilities this package does not include, each previewed with its served
   * price beside the surface it belongs to. Empty when nothing is withheld.
   */
  paidBoundaries: readonly FmOperationalPaidBoundaryDescriptor[];
  dialogs: readonly FmOperationalDialogDescriptor[];
  formFlow?: FmOperationalFormFlowCapabilityDescriptor;
  renderFailure?: FmOperationalRenderFailureDescriptor;
  /** Blocks the workspace while route orchestration replaces an invalid URL. */
  routePending?: boolean;
  emptyLabel: string;
  onEvent?: (event: FmOperationalWorkspaceEvent) => void;
}

/**
 * Discriminated on `state`: each non-ready state carries the typed surface the
 * renderer needs to show a designed state instead of one grey sentence — a
 * named scope, its own copy, and the control that resolves it.
 */
export type FmOperationalWorkspaceDescriptor =
  | (FmOperationalWorkspaceBase & { state: "ready" })
  | (FmOperationalWorkspaceBase & { state: "loading" })
  | (FmOperationalWorkspaceBase & {
      state: "empty";
      empty: FmOperationalEmptyStateDescriptor;
    })
  | (FmOperationalWorkspaceBase & {
      state: "stale";
      stale: FmOperationalStaleStateDescriptor;
    })
  | (FmOperationalWorkspaceBase & {
      state: "blocked";
      blocked: FmOperationalBlockerDescriptor;
    })
  | (FmOperationalWorkspaceBase & {
      state: "error";
      error: FmOperationalErrorStateDescriptor;
    });

/** The ready branch, for fixtures and builders that always produce it. */
export type FmOperationalReadyWorkspaceDescriptor = Extract<
  FmOperationalWorkspaceDescriptor,
  { state: "ready" }
>;

export interface FmOperationalWorkspaceRootProps {
  descriptor: FmOperationalWorkspaceDescriptor;
  /** Compact DS-owned rendering inside a governed host dialog. */
  presentation?: "page" | "dialog";
  "data-testid"?: string;
}
